/**
 * Safely parses a JSON string and returns a fallback value if parsing fails.
 * @param data The JSON string to parse.
 * @param fallback The value to return if parsing fails.
 * @returns The parsed object or the fallback value.
 */
export function jsonSafeParse<T = Record<string, unknown>>(data: string, fallback: T = {} as T): T {
  try {
    return JSON.parse(data || '{}') as T;
  } catch {
    return fallback as T;
  }
}

export function isObject(value: unknown) {
  return Object.prototype.toString.call(value) === '[object Object]';
}

/**
 * Helper to clean up extra whitespace in all string values of an object
 */
export const cleanAllWhitespace = <T extends Record<string, unknown>>(obj: T): T => {
  return Object.fromEntries(
    Object.entries(obj).map(([key, value]) => [
      key,
      typeof value === 'string' ? value.replace(/\s+/g, ' ').trim() : value,
    ])
  ) as T;
};

/**
 * Helper to filter out undefined values from an object
 */
export const removeUndefinedValues = <T extends Record<string, unknown>>(obj: T) => {
  return Object.fromEntries(Object.entries(obj).filter(([, value]) => value !== undefined)) as Partial<T>;
};
